<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { endOfDay, format, startOfDay } from 'date-fns';
import updateUrl from '@/service/updateUrl';
import { type PermissionGroupType, useDetectPermissions } from '@/composables/useDetectPermissions';
import _ from '@/setup/lodash';
import { useApi } from '@/api';
import { useLs } from '@/setup/vueLs';
import { useTypedStore } from '@/store';
import { usePopupManager } from '@/components/PopupManager.vue';
import MediaBuyingInfo from './components/MediaBuyingInfo.vue';
import type { MediaBuyingModerationStatusSections } from '@/types/status';
import type { GetMediaBuyingPaymentsByIdData, GetMediaBuyingPaymentsByIdResponse, PostMediaBuyingPaymentsByIdCommentsData } from '@/types/openapi-ts';
//#endregion

//#region types
type MediaBuyingPaymentsQuery = NonNullable<GetMediaBuyingPaymentsByIdData['query']>;
type MediaBuyingPaymentsPermissions = NonNullable<GetMediaBuyingPaymentsByIdResponse['misc']>['permissions'];

type ConfirmAction = 'approve' | 'initialPayment' | 'paid' | 'decline' | 'failed' | 'rework' | 'review';
//#endregion

//#region const
const LS_PATH = 'MediaBuying/Payments';

const mapStatus = {
  approve: 'declined',
  decline: 'approved',
  'for-rework': 'for_rework',
  'to-review': 'on_review',
  failed: 'failed',
  paid: 'paid',
  'not-paid': 'not_paid',
  'start-processing': 'processing',
};
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const router = useRouter();
const store = useTypedStore();
const ls = useLs();
const popupManager = usePopupManager();
const { t } = useI18n();
const { checkRequest, checkRoute } = useDetectPermissions();
//#endregion

//#region state
const isDataLoading = ref(true);
const info = ref({} as GetMediaBuyingPaymentsByIdResponse['payload']);
const permissions = ref({} as MediaBuyingPaymentsPermissions);
const currencyCode = ref('');
const periodStats = ref<any>(null);
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const currentAcl = computed(() => store.getters['auth/currentAcl']);

const fromPage = computed(() => {
  return route.query.from;
});

const paymentId = computed(() =>{
  return route.params.id as string;
});

const hideToolbar = computed(() => {
  const hideFailed = info.value.moderation_status === 'approved'
    && info.value.payout_status !== 'failed';
  const hidePaid = info.value.moderation_status === 'approved'
    && info.value.payout_status !== 'paid';
  const isUltimate = info.value.moderation_status !== 'declined'
    && info.value.moderation_status !== 'approved';
  const forUser = !adminAcl.value.is_superuser
    && permissions.value.review_actions
    && (isUltimate || (hidePaid && hideFailed));
  const forAdmin = adminAcl.value.is_superuser && (isUltimate || (hidePaid && hideFailed));
  return forUser || forAdmin;
});

const backPage = computed(() => {
  if (fromPage.value === 'media_buying_payments_moderation' || info.value.moderation_status !== 'approved') {
    return {
      title: `${t('mediaBuying.page_title.media_buying_payments_moderation.name')} — ${t(`mediaBuying.page_title.media_buying_payments_moderation.${info.value.moderation_status}`)}`,
      link: `/media-buying/payments-moderation/${info.value.moderation_status?.replace('_', '-')}`,
    };
  } else {
    return {
      title: `${t('mediaBuying.page_title.media_buying_payments_payout.name')} — ${t(`mediaBuying.page_title.media_buying_payments_payout.${info.value.payout_status}`)}`,
      link: `/media-buying/payments-payout/${info.value.payout_status?.replace('_', '-')}`,
    };
  }
});

const breadcrumbs = computed(() => {
  return [
    backPage.value,
    {
      title: info.value.affiliate_email,
    },
  ];
});

const showActions = computed(() => {
  const moderationStatus = info.value?.moderation_status;
  const payoutStatus = info.value?.payout_status;

  const isOnReview = moderationStatus === 'on_review';
  const isApproved = moderationStatus === 'approved';

  return {
    approve: isOnReview,
    decline: isOnReview,
    paid: isApproved && payoutStatus === 'processing',
    failed: isApproved && payoutStatus === 'processing',
    initiatePayment: isApproved && payoutStatus === 'not_paid',
    rework: isOnReview || (isApproved && (payoutStatus === 'not_paid' || payoutStatus === 'processing')),
    review: moderationStatus === 'for_rework' || (isApproved && payoutStatus === 'for_rework'),
  };
});
//#endregion

//#region methods
const _query = () => {
  if(periodStats.value) {
    const { stats_period_start, stats_period_end } = periodStats.value;

    return {
      stats_period_start: stats_period_start ? format(startOfDay(stats_period_start), 'yyyy-MM-dd HH:mm:ss') : '',
      stats_period_end: stats_period_end ? format(endOfDay(stats_period_end), 'yyyy-MM-dd HH:mm:ss') : '',
    } as MediaBuyingPaymentsQuery;
  }

  return {} as MediaBuyingPaymentsQuery;
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._query(),
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData() {
  if (!paymentId.value) return;

  isDataLoading.value = true;

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getMediaBuyingPaymentsInfo(paymentId.value, options);
    permissions.value = misc!.permissions;
    currencyCode.value = misc!.currency_code;
    info.value = payload;

    const isModeration = fromPage.value === 'media_buying_payments_moderation';
    checkRoute(
      'media_buying_payments_view',
      fromPage.value as PermissionGroupType,
      isModeration ? info.value.moderation_status : info.value.payout_status,
    );
  } finally {
    isDataLoading.value = false;
  }
}

async function initData() {
  if (checkRequest('media_buying_payments_view')){
    const _urlData = updateUrl.getParseParams(route.hash);

    const period = !_.isEmpty(_urlData) ? _urlData : ls.get(`${LS_PATH}/period`) || null;
    if(period) {
      const { stats_period_start, stats_period_end } = period;
      if (stats_period_start && stats_period_end) {
        periodStats.value = {
          stats_period_start,
          stats_period_end,
        };
      }
    }

    nextTick(async () => {
      await initNeedsAttention();
      fetchData();
    });
  }
  checkRoute('media_buying_payments_view');
}

async function markAsRead() {
  if (info.value.needs_attention) {
    await api.postMediaBuyingPaymentMarkAsRead(info.value.id);
  } else {
    await api.postMediaBuyingPaymentMarkAsUnread(info.value.id);
  }
  fetchData();
}

async function initNeedsAttention() {
  if (route.query.needs === 'success') {
    await api.postMediaBuyingPaymentMarkAsRead(route.params.id as string);
    router.replace({
      path: route.path,
      query: { ...route.query, needs: undefined },
    });
  }
}

function applyCampaignStatistics(period: string[]) {
  if (period) {
    const [stats_period_start, stats_period_end] = period;
    periodStats.value = {
      stats_period_start,
      stats_period_end,
    };
    ls.set(`${LS_PATH}/period`, {
      stats_period_start,
      stats_period_end,
    });
  } else {
    periodStats.value = null;
    ls.set(`${LS_PATH}/period`, null);
  }

  fetchData();
}

function approveHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.approve_request.text'),
      title: t('mediaBuying.info.popup.approve_request.title'),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('mediaBuying.info.popup.approve_request.button'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        onConfirm('approve');
        context.handleClose();
      },
    },
  });
}

function paidHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.paid_request.text'),
      title: t('mediaBuying.info.popup.paid_request.title'),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.info.popup.paid_request.button'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        onConfirm('paid');
        context.handleClose();
      },
    },
  });
}

function initiatePaymentHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.initiate_request.text'),
      title: t('mediaBuying.info.popup.initiate_request.title'),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.info.popup.initiate_request.button'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        onConfirm('initialPayment');
        context.handleClose();
      },
    },
  });
}

function reworkHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.rework_request.text'),
      title: t('mediaBuying.info.popup.rework_request.title'),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('mediaBuying.info.popup.rework_request.button'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        onConfirm('rework');
        context.handleClose();
      },
    },
  });
}

function reviewHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.on_review_request.text'),
      title: t('mediaBuying.info.popup.on_review_request.title'),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('mediaBuying.info.popup.on_review_request.button'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'), //+
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        onConfirm('review');
        context.handleClose();
      },
    },
  });
}

function declineHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.decline_request.text'),
      title: t('mediaBuying.info.popup.decline_request.title'),
      bodyIcon: 'ban',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.info.popup.decline_request.button'),
      confirmType: 'negative',
      confirmIcon: 'ban',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        onConfirm('decline');
        context.handleClose();
      },
    },
  });
}

function failedHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.failed_request.text'),
      title: t('mediaBuying.info.popup.failed_request.title'),
      bodyIcon: 'ban',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.info.popup.failed_request.button'),
      confirmType: 'negative',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        onConfirm('failed');
        context.handleClose();
      },
    },
  });
}

function onConfirm(confirmAction: ConfirmAction) {
  const { moderation_status: modStatus, payout_status: payStatus } = info.value;

  const simpleActions: Record<string, [MediaBuyingModerationStatusSections, keyof typeof mapStatus]> = {
    approve: ['moderation', 'approve'],
    initialPayment: ['payout', 'start-processing'],
    paid: ['payout', 'paid'],
    decline: ['moderation', 'decline'],
    failed: ['payout', 'failed'],
  };

  if (simpleActions[confirmAction]) {
    const [target, status] = simpleActions[confirmAction];
    return changeStatus(target, status);
  }

  if (confirmAction === 'rework') {
    const isApproved = modStatus === 'approved';
    const isProcessingOrNotPaid = payStatus === 'not_paid' || payStatus === 'processing';

    return isApproved && isProcessingOrNotPaid
      ? changeStatus('payout', 'for-rework')
      : changeStatus('moderation', 'for-rework');
  }

  if (confirmAction === 'review') {
    return modStatus === 'approved' && payStatus === 'for_rework'
      ? changeStatus('payout', 'not-paid')
      : changeStatus('moderation', 'to-review');
  }
}

async function changeStatus(section: MediaBuyingModerationStatusSections, status: keyof typeof mapStatus) {
  await api.postMediaBuyingPaymentChangeStatus(paymentId.value, section, status);
  const from = `media_buying_payments_${info.value.moderation_status === 'approved' ? 'payout' : 'moderation'}`;
  const statusesVisible = `${from}_statuses_visible` as keyof typeof currentAcl.value;
  const hasAclAccess = currentAcl.value[statusesVisible]?.includes(mapStatus[status]);

  if (adminAcl.value.is_superuser || hasAclAccess) {
    await fetchData();
  } else {
    const targetSection = fromPage.value === 'media_buying_payments_moderation' ? 'moderation' : 'payout';
    const currentStatus = info.value[`${targetSection}_status` as keyof typeof info.value] as string;
    const formattedStatus = currentStatus.replace('_', '-');

    await router.push(`/media-buying/payments-${targetSection}/${formattedStatus}`);
  }
}

function postComment(comment: PostMediaBuyingPaymentsByIdCommentsData['body']) {
  return api.postMediaBuyingPaymentComments(paymentId.value, comment);
}

function getComments() {
  return api.getMediaBuyingPaymentComments(paymentId.value);
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <MediaBuyingInfo
    class="media-buying-payments-info"
    :breadcrumbs="breadcrumbs"
    :is-loading="isDataLoading"
    :info="info"
    :currency-code="currencyCode"
    :period="periodStats"
    :permissions="permissions"
    :get-comments="getComments"
    :post-comment="postComment"
    type="payment"
    @apply-filters="applyCampaignStatistics"
  >
    <template v-slot:buttons>
      <template v-if="hideToolbar">
        <UiButton
          v-if="showActions.approve"
          icon="check"
          filled
          @click="approveHandler()"
        >
          {{ $t('mediaBuying.actions.approve') }}
        </UiButton>
        <UiButton
          v-if="showActions.paid"
          icon="check"
          filled
          @click="paidHandler()"
        >
          {{ $t('mediaBuying.actions.paid') }}
        </UiButton>
        <UiButton
          v-if="showActions.initiatePayment"
          icon="check"
          filled
          @click="initiatePaymentHandler()"
        >
          {{ $t('mediaBuying.actions.initiate_payment') }}
        </UiButton>
        <UiButton
          v-if="showActions.rework"
          icon="sync-alt"
          @click="reworkHandler()"
        >
          {{ $t('mediaBuying.actions.send_for_rework') }}
        </UiButton>
        <UiButton
          v-if="showActions.review"
          icon="check-double"
          @click="reviewHandler()"
        >
          {{ $t('mediaBuying.actions.send_to_review') }}
        </UiButton>
      </template>
      <UiButton
        :icon="info.needs_attention ? 'circle-exclamation-check' : 'exclamation-triangle'"
        :type="info.needs_attention ? 'positive' : 'negative'"
        @click="markAsRead"
      >
        {{ !info.needs_attention ? $t('mediaBuying.other.btn_mark') : $t('mediaBuying.other.btn_unmark') }}
      </UiButton>
      <template v-if="hideToolbar">
        <UiButton
          v-if="showActions.decline"
          icon="times"
          type="negative"
          @click="declineHandler()"
        >
          {{ $t('mediaBuying.actions.decline') }}
        </UiButton>
        <UiButton
          v-if="showActions.failed"
          icon="times"
          type="negative"
          @click="failedHandler()"
        >
          {{ $t('mediaBuying.actions.failed') }}
        </UiButton>
      </template>
    </template>
  </MediaBuyingInfo>
</template>
